import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate, RouterProvider, useParams } from 'react-router-dom';
import { BASE_PATH } from '@/common/basePath';
import Sidebar from '@/components/Sidebar';
import OverviewPage from '@/pages/Overview';
import JobListPage from '@/pages/JobList';
import JobTaskListPage from '@/pages/JobTaskList';
import SceneManagementPage from '@/pages/SceneManagement';
import AddSceneToTreePage from '@/pages/AddSceneToTree';
import TagManagementPage from '@/pages/TagManagement';
import AddSceneToTagPage from '@/pages/AddSceneToTag';
import SceneGenerationPage from '@/pages/SceneGeneration';
import SceneGenerationJobTasksPage from '@/pages/SceneGenerationJobTasks';
import ForeignSyncTasksPage from '@/pages/SceneGeneration/ForeignSyncTasksPage';
import SceneBosFilesPage from '@/pages/SceneBosFiles';
import SceneAnnotatePage from '@/pages/SceneAnnotate';
import SceneQcListPage from '@/pages/SceneQcList';
import PredictionUpdateJobTasksPage from '@/pages/PredictionUpdateJobTasks';
import DataUpdatePage from '@/pages/DataUpdate';
import PerceptionRefreshScenesPage from '@/pages/PerceptionRefreshScenes';
import SimDashboardPage from '@/pages/SimDashboard';
import DailySimulationPage from '@/pages/DailySimulation';
import PerceptionReportPage from '@/pages/PerceptionReport';
import ModelManagementPage from '@/pages/ModelManagement';
import ScheduleListPage from '@/pages/Schedule/List';
import ReleaseTriggersPage from '@/pages/Schedule/ReleaseTriggers';
import UserLoginPage from '@/pages/UserLogin';
import PassgateCallbackPage from '@/pages/PassgateCallback';
import AdminPage from '@/pages/Admin';
import SceneCollectionPage from '@/pages/SceneCollection';
import SysConfigPage from '@/pages/SysConfig';
import AccountSettingsPage from '@/pages/AccountSettings';
import AboutPage from '@/pages/About';
import { RequirePermission } from '@/common/auth/Guard';

const HILNativeTasksPage = lazy(() => import('@/pages/HIL/NativeTasks'));
const HILReportsPage = lazy(() => import('@/pages/HIL/Reports'));
const HILBenchDirectoryPage = lazy(() => import('@/pages/HIL/BenchDirectory'));
const HILBvtTaskListPage = lazy(() => import('@/pages/HIL/Bvt/TaskList'));
const HILBvtTaskDetailPage = lazy(() => import('@/pages/HIL/Bvt/TaskDetail'));
const BvtCasesPage = lazy(() => import('@/pages/HIL/Bvt/Cases'));
const BvtCheckersPage = lazy(() => import('@/pages/HIL/Bvt/Checkers'));
const HIL_PAGE_LOADING = (
  <div
    role="status"
    aria-live="polite"
    style={{ minHeight: '100vh', display: 'grid', placeItems: 'center' }}
  >
    页面加载中…
  </div>
);

/** 兼容旧 /scene/:sceneId 详情页链接:自动跳到 /scene-annotate?sceneId= 主编辑面板
 *  (SceneAnnotate 是主入口;SceneBosFiles 仅保留用于 BOS 文件浏览 / 另存为 /
 *  删除派生场景等尚未搬进 SceneAnnotate 的次要操作,通过 header 上「其他操作」链接进入)。 */
function LegacySceneRedirect() {
  const { sceneId } = useParams<{ sceneId: string }>();
  return <Navigate to={`/scene-annotate?sceneId=${sceneId ?? ''}`} replace />;
}

/** 旧 /hil/bvt/tasks/:taskId → /bvt/tasks/:taskId（保留查询串）。 */
function LegacyBvtTaskRedirect() {
  const { taskId } = useParams<{ taskId: string }>();
  return <Navigate to={`/bvt/tasks/${encodeURIComponent(taskId ?? '')}${window.location.search}`} replace />;
}

// 左栏信息架构 v2 —— docs/platform-refactor-v1.md §1
// - 登录后 landing 到 /overview
// - 规控/感知任务列表统一走 /task-list?sim_type=<control|perception>
// - Daily 同理 /daily-simulation?sim_type=<...>
// - 计划任务从原来的 JobList 顶部 Tab 提为一级路由 /schedule/*
const router = createBrowserRouter([
  {
    path: '/user-login',
    element: <UserLoginPage />,
  },
  {
    path: '/callback/login',
    element: <PassgateCallbackPage />,
  },
  {
    path: '/',
    element: <Sidebar />,
    children: [
      // 登录后 landing 到 /overview (概览页顶部两张仿真类型卡 + 项目资源占用卡)。
      // 详见 docs/overview-project-resource.md。
      { path: '', element: <Navigate to="/overview" replace /> },
      { path: 'overview', element: <OverviewPage /> },

      // ── 仿真看板（全平台运营数据 dashboard）──
      // 独立权限点 menu:sim-dashboard：只有被授权用户可见菜单/进页面/取数。
      // 菜单可见性由 Sidebar 侧同一权限点决定，这里 RequirePermission 兜底防 URL
      // 直填，后端 /sim-dashboard router 级 require_permission 三层同码硬门。
      {
        path: 'sim-dashboard',
        element: (
          <RequirePermission code="menu:sim-dashboard">
            <SimDashboardPage />
          </RequirePermission>
        ),
      },

      // ── 规控 / 感知 仿真任务 ──
      // 老路由 /task-list/:taskTypeId 仍保留兼容，JobList 内部会把 params.taskTypeId 与 query 一起消费
      { path: 'task-list/:taskTypeId', element: <JobListPage /> },
      { path: 'task-list', element: <JobListPage /> },
      { path: 'job-tasks/:jobId', element: <JobTaskListPage /> },

      // ── HIL ──
      { path: 'hil', element: <Navigate to="/hil/native/tasks" replace /> },
      { path: 'hil/native/tasks', element: <Suspense fallback={HIL_PAGE_LOADING}><HILNativeTasksPage /></Suspense> },
      { path: 'hil/native/tasks/:taskId', element: <Suspense fallback={HIL_PAGE_LOADING}><HILNativeTasksPage /></Suspense> },
      { path: 'hil/reports', element: <Suspense fallback={HIL_PAGE_LOADING}><HILReportsPage /></Suspense> },
      // 台架管理（只读目录）：平台目录 ↔ 执行端实时状态的对照视图。
      // 与 /hil/native/benches（建 Job 台架候选）是两条独立链路，本页不改调度、不写。
      { path: 'hil/resources/directory', element: <Suspense fallback={HIL_PAGE_LOADING}><HILBenchDirectoryPage /></Suspense> },
      // 旧 BVT 地址（曾挂在 HIL 下）重定向到顶层 /bvt
      { path: 'hil/bvt', element: <Navigate to="/bvt/tasks" replace /> },
      { path: 'hil/bvt/tasks', element: <Navigate to="/bvt/tasks" replace /> },
      { path: 'hil/bvt/tasks/:taskId', element: <LegacyBvtTaskRedirect /> },
      { path: 'hil/bvt/catalog', element: <Navigate to="/bvt/cases" replace /> },

      // ── BVT 回灌 ──
      { path: 'bvt', element: <Navigate to="/bvt/tasks" replace /> },
      { path: 'bvt/tasks', element: <Suspense fallback={HIL_PAGE_LOADING}><HILBvtTaskListPage /></Suspense> },
      { path: 'bvt/tasks/:taskId', element: <Suspense fallback={HIL_PAGE_LOADING}><HILBvtTaskDetailPage /></Suspense> },
      { path: 'bvt/cases', element: <Suspense fallback={HIL_PAGE_LOADING}><BvtCasesPage /></Suspense> },
      { path: 'bvt/checkers', element: <Suspense fallback={HIL_PAGE_LOADING}><BvtCheckersPage /></Suspense> },

      // ── Daily ──
      { path: 'daily-simulation', element: <DailySimulationPage /> },

      // ── 感知评估报表 ──
      // 独立路由而非挂在 /daily-simulation 下：数据源不同（这里按 jobId 取数、
      // 不挂 source=daily 闸门，感知 job 的 source 是 planning_perception(4)）。
      // ?jobId=&baseJobId= 决定单 job / 对比两种形态，链接可直接分享。
      { path: 'perception-report', element: <PerceptionReportPage /> },

      // ── 模型管理 ──
      // 3 个子页 (主版本查询 / 模型查询 / 模型上传); 顶层 /model-management 保留
      // 兼容旧链接,重定向到 releases 子页。
      { path: 'model-management', element: <Navigate to="/model-management/releases" replace /> },
      { path: 'model-management/releases', element: <ModelManagementPage /> },
      { path: 'model-management/models', element: <ModelManagementPage /> },
      { path: 'model-management/upload', element: <ModelManagementPage /> },
      { path: 'model-management/sync', element: <ModelManagementPage /> },

      // ── 场景 ──
      { path: 'scene-management', element: <SceneManagementPage /> },
      { path: 'tag-management', element: <TagManagementPage /> },
      { path: 'add-scenes-to-tree/:nodeId', element: <AddSceneToTreePage /> },
      { path: 'add-scenes-to-tag/:tagId', element: <AddSceneToTagPage /> },
      { path: 'scene-generation', element: <SceneGenerationPage /> },
      { path: 'scene-generation/job-tasks/:jobId', element: <SceneGenerationJobTasksPage /> },
      { path: 'scene-generation/foreign-sync-tasks/:dispatchJobId', element: <ForeignSyncTasksPage /> },
      {
        // 场景质检子页面：需要 menu:scene-qc 权限；无权限时 <RequirePermission> 兜底
        //（防 URL 直填绕过 Sidebar 菜单过滤）。菜单可见性由 Sidebar 侧同一权限点决定。
        path: 'scene-qc',
        element: (
          <RequirePermission code="menu:scene-qc">
            <SceneQcListPage />
          </RequirePermission>
        ),
      },
      {
        // 数据更新子页面（PR_DATA）：照场景质检范式，需要 menu:prediction:update 权限；
        // 无权限时 <RequirePermission> 兜底（防 URL 直填绕过 Sidebar 菜单过滤）。
        // 任务列表拆为独立下钻页 prediction-update/job-tasks/:jobId（不复用通用 JobTaskList，
        // 去掉评估器筛选 / 建任务 / 按场景分类 / 未通过跳转 / 对比按钮等冗余功能）。
        // 页面分「预测数据（PR_DATA）」「感知数据（PE_DATA）」两个页签，见 pages/DataUpdate。
        path: 'prediction-update',
        element: (
          <RequirePermission code="menu:prediction:update">
            <DataUpdatePage />
          </RequirePermission>
        ),
      },
      // 预测数据迭代任务列表（PR_DATA 专属独立页）：从数据更新页 Job 名下钻，
      // 展示任务表 + 迭代状态 / 迭代版本号两列。/prediction-update 前缀使左侧菜单高亮自动命中。
      { path: 'prediction-update/job-tasks/:jobId', element: <PredictionUpdateJobTasksPage /> },
      // 感知数据刷写（PE_DATA）场景明细：从数据更新页「感知数据」页签下钻。/prediction-update
      // 前缀让左侧「数据更新」菜单保持高亮；权限与数据更新页相同。
      {
        path: 'prediction-update/perception/jobs/:jobId',
        element: (
          <RequirePermission code="menu:prediction:update">
            <PerceptionRefreshScenesPage />
          </RequirePermission>
        ),
      },
      // 场景配置文件页(SceneBosFiles):BOS 文件浏览 + 云控事件编辑 + 另存为等主编辑入口。
      // 从场景管理 / 场景生成 / 任务对比 三处 navigate 进入,页面按 ?from= 决定「返回」目的地。
      { path: 'scene-bos-files/:sceneId', element: <SceneBosFilesPage /> },
      // 场景标注 · 单场景 description / 时间 / 地图 / 标签 编辑面板;
      // 从任务列表(JobTaskList / 全量场景列表)按 sceneId 进入,一期只放开单场景编辑,
      // 批量 codes 入口(SceneAnnotateCard) 待后端 annotate-submit 接口就绪后放开。
      { path: 'scene-annotate', element: <SceneAnnotatePage /> },
      // 兼容:旧 /scene/:sceneId 详情页链接自动跳转到 SceneBosFiles 主编辑页
      { path: 'scene/:sceneId', element: <LegacySceneRedirect /> },

      // ── 计划任务（从 JobList 三 Tab 提为一级） ──
      { path: 'schedule', element: <Navigate to="/schedule/list" replace /> },
      { path: 'schedule/list', element: <ScheduleListPage /> },
      { path: 'schedule/release-triggers', element: <ReleaseTriggersPage /> },

      // ── 系统 ──
      {
        path: 'admin',
        element: (
          <RequirePermission code="menu:admin">
            <AdminPage />
          </RequirePermission>
        ),
      },
      {
        // 场景集合运维：原后台管理页的「场景集合」Tab,拆成独立权限点后提为一级页面。
        // 与 /admin 同构：Sidebar 按同一权限点过滤菜单,这里 RequirePermission 兜底
        // 防 URL 直填,后端 /scene-tree/lock|hide 挂同码硬门。
        path: 'scene-collection',
        element: (
          <RequirePermission code="menu:scene-collection">
            <SceneCollectionPage />
          </RequirePermission>
        ),
      },
      {
        // 系统参数运维：原后台管理页的「参数配置」Tab,同上拆为独立权限点 + 一级页面。
        path: 'sys-config',
        element: (
          <RequirePermission code="menu:sys-config">
            <SysConfigPage />
          </RequirePermission>
        ),
      },
      { path: 'account-settings', element: <AccountSettingsPage /> },
      { path: 'about', element: <AboutPage /> },
    ],
  },
], {
  // 挂在子路径下部署（vite `base` 非 `/`）时必须给路由带 basename，
  // 否则浏览器路径带着前缀、任何路由都匹配不上（表现为 "No route matches URL" 错误页）。
  basename: BASE_PATH || '/',
});

export const AppRouter = () => {
  return <RouterProvider router={router} />;
};
